import { z } from 'zod';
import { CLASSIFICATION_MIN_VISIBLE_CONFIDENCE } from '../constants';
import { EmailPreviewSchema } from './common';

/**
 * Classification Enums & Core Types
 */

export const TrackSchema = z.enum(['Attention', 'Archive', 'Trash'])
export type Track = z.infer<typeof TrackSchema>

export const ActionTypeSchema = z.enum([
  'pay_bill',
  'verify_identity',
  'reset_password',
  'reply',
  'sign_document',
  'attend_meeting',
  'provide_info',
  'schedule',
  'none',
])
export type ActionType = z.infer<typeof ActionTypeSchema>

export const RiskTypeSchema = z.enum([
  'none',
  'security',
  'financial',
  'legal_tax',
  'health',
  'service_interruption',
]);
export type RiskType = z.infer<typeof RiskTypeSchema>;

export const AuthEventTypeSchema = z.enum([
  'code_challenge',
  'login_event',
  'credential_change',
  'access_grant',
  'none',
]);
export type AuthEventType = z.infer<typeof AuthEventTypeSchema>;

export const ObligationStateSchema = z.enum(['open', 'closed', 'none']);
export type ObligationState = z.infer<typeof ObligationStateSchema>;

export const ContentDispositionSchema = z.enum([
  'actionable',
  'retain_reference',
  'disposable_promotional',
  'none',
]);
export type ContentDisposition = z.infer<typeof ContentDispositionSchema>;

/**
 * Classification Signals
 * Structured data extracted from email and sender insights
 */

export const ClassificationSignalsSchema = z.object({
  is_list_mail: z.boolean().describe('Email is from a mailing list'),
  is_marketing: z.boolean().describe('Email appears to be marketing/promotional'),
  requires_action: z.boolean().describe('Email requires user action'),
  action_type: ActionTypeSchema.describe('Type of action required if any'),
  auth_event_type: AuthEventTypeSchema
    .nullable()
    .optional()
    .describe('Authentication event extracted from the message (null when absent or invalid)'),
  obligation_state: ObligationStateSchema
    .nullable()
    .optional()
    .describe('Explicit obligation state in the message (null when absent or invalid)'),
  content_disposition: ContentDispositionSchema
    .nullable()
    .optional()
    .describe('Content-only disposition extracted from the message (null when absent or invalid)'),
  // #283 finding #6 - risk_type and is_transaction_record must NOT be
  // synthesised by Pre-LLM evaluation (which had no signal to derive them from).
  // Schema is nullable so Pre-LLM can leave them null and only the LLM /
  // matched rule populates them when there's actual evidence.
  risk_type: RiskTypeSchema
    .nullable()
    .describe('Security/financial/legal risk level (null when not classified)'),
  is_transaction_record: z
    .boolean()
    .nullable()
    .describe('Email is a transaction confirmation or receipt (null when not classified)'),
  institution_type: z
    .enum(['banking', 'insurance', 'government'])
    .nullable()
    .describe('Institution type (banking, insurance, government) when detected'),
  event_status: z
    .enum(['pending', 'in_progress', 'expired', 'closed', 'na', 'unknown'])
    .describe('Lifecycle state of time-based event'),
  event_type: z
    .enum(['delivery', 'deadline', 'calendar_event', 'subscription', 'payment', 'other'])
    .nullable()
    .describe('Category of time-based event if applicable'),
  event_date: z
    .string()
    .datetime()
    .nullable()
    .optional()
    .describe('ISO timestamp of key event date if determinable'),
  event_date_parts: z
    .object({
      year: z.number().int().min(1970).max(2100).nullable(),
      month: z.number().int().min(1).max(12).nullable(),
      day: z.number().int().min(1).max(31).nullable(),
      hour: z.number().int().min(0).max(23).nullable(),
      minute: z.number().int().min(0).max(59).nullable(),
      tz: z.string().max(40).nullable(),
      precision: z.enum(['year', 'month', 'day', 'hour', 'minute']),
    })
    .nullable()
    .optional()
    .describe('Literal date components extracted by the LLM before deterministic time derivation'),
  event_relative_parts: z
    .object({
      anchor: z.literal('received_at'),
      offset_value: z.number().int().min(-3660).max(3660),
      offset_unit: z.enum(['calendar_days', 'hours', 'minutes']),
      hour: z.number().int().min(0).max(23).nullable(),
      minute: z.number().int().min(0).max(59).nullable(),
      tz: z.string().max(40).nullable(),
      precision: z.enum(['day', 'hour', 'minute']),
      source_text: z.string().min(1).max(200),
    })
    .nullable()
    .optional()
    .describe('Relative event expression anchored to received_at for deterministic resolution'),
  key_evidence: z
    .array(z.string())
    .describe('Evidence snippets supporting the classification'),
  needs_user_action: z
    .boolean()
    .optional()
    .describe('Whether the user must take an explicit action (stricter than requires_action)'),
  has_future_event: z
    .boolean()
    .optional()
    .describe('Whether the email references a future event/deadline'),
  safety_block_reason: z
    .enum(['high_risk_trash', 'future_event_trash', 'unresolved_risk', 'otp_active'])
    .nullable()
    .optional()
    .describe('Reason the DecisionSafetyLayer blocked a Trash decision (null if not blocked)'),
})

export type ClassificationSignals = z.infer<typeof ClassificationSignalsSchema>

/**
 * Decision provenance (issue #721)
 *
 * Additive, reconstructable provenance describing HOW a classification was
 * decided. Persisted alongside every finalized suggestion so maintainers can
 * reason about a verdict without replaying ephemeral logs. These are strictly
 * additive to the legacy `phase1_match` / `llm_used` / `matched_rule_id` /
 * `signals.safety_block_reason` fields, which are kept unchanged for
 * compatibility (see docs/CLASSIFICATION_AND_LABELING.md glossary).
 */

/**
 * Canonical classifier source. Maps from the internal `ClassifierSource`
 * pipeline value:
 *   phase1  -> pre_llm        (Pre-LLM evaluation)
 *   phase2a -> llm_advisory   (LLM advisory)
 *   phase2b -> post_llm_rule  (Post-LLM resolution: rule override)
 *   policy  -> post_llm_policy (Post-LLM resolution: code-backed policy)
 */
export const DecisionSourceSchema = z.enum([
  'pre_llm',
  'llm_advisory',
  'post_llm_rule',
  'post_llm_policy',
])
export type DecisionSource = z.infer<typeof DecisionSourceSchema>

/**
 * OverrideMatrix decision path, persisted verbatim. The emitted strings are
 * kept for compatibility (`phase1_only`, `phase1_llm_override`); the canonical
 * display aliases (`pre_llm_only`, `llm_rule_override`) are accepted so future
 * writers can migrate without breaking readers. `phase1_llm_agree` was removed
 * as unreachable (issue #721).
 */
export const DecisionPathSchema = z.enum([
  'pre_llm_only',
  'phase1_only',
  'llm_only',
  'llm_rule_override',
  'phase1_llm_override',
  'safety_blocked',
])
export type DecisionPath = z.infer<typeof DecisionPathSchema>

/**
 * Full veto + downgrade reason set produced by the DecisionSafetyLayer.
 * Superset of the legacy 4-value `signals.safety_block_reason` enum, which is
 * NOT widened (legacy readers keep working; new readers use this).
 */
export const SafetyReasonCodeSchema = z.enum([
  // Veto (Trash -> Attention)
  'high_risk_trash',
  'future_event_trash',
  'otp_active',
  'unresolved_risk',
  'government_trash',
  'active_delivery_trash',
  'phase1_guardrail_trash',
  // Issue #770: the Trash relationship/engagement floor is read-aware. When the
  // email is still UNREAD the safer destination is Attention, which in this
  // layer's vocabulary is a VETO - so these two codes live in the veto block
  // (and in `SAFETY_VETO_CODES`) to keep the #721 veto/downgrade analytics
  // correct.
  'relationship_trash_unread',
  'engagement_trash_unread',
  // Downgrade (Attention -> Archive, or Trash -> Archive)
  // Issue #770: `read_attention` is a shipped SafetyDowngradeReason that was
  // missing from this enum, so any reader parsing a persisted `read_attention`
  // failed validation. Added here to close that drift.
  'read_attention',
  'marketing_without_action',
  'missing_label_attention',
  'missing_rationale_attention',
  'low_confidence_attention',
  'inconsistent_signals_attention',
  'attention_obligation_not_met',
  'relationship_trash',
  // Issue #770: engagement-based Trash floor (read -> Archive).
  'engagement_trash',
  'stale_unread_attention',
  'unreviewed_trash_obligation',
  'vetoed_attention_downgraded',
])
export type SafetyReasonCode = z.infer<typeof SafetyReasonCodeSchema>

/**
 * Additive, read-time-derived explanation tags (issue #1017). Explain *why* a
 * suggestion stayed visible / was deferred / was protected, without exposing
 * raw internal fields (`risk_type`, `action_type`, `safety_reason_code`
 * values, or a numeric confidence figure) directly to the UI.
 *
 * Hard invariant: these are a pure projection computed strictly AFTER
 * `finalizeLlmAdvisory()` from already-persisted/available fields. They are
 * NEVER an input to Pre-LLM rules, the LLM advisory, Post-LLM resolution, the
 * DecisionSafetyLayer, ConfidenceCalibrator, or OverrideMatrix, and must never
 * change `track`, `confidence`, `matched_rule_id`, or `decision_source`.
 *
 * OTP tags (`expired_code` / `fresh_code`) are derived from persisted
 * `signals.auth_event_type` + `received_at` + final track - not from
 * pipeline-only `EmailFeatures.is_otp_email`. `in_contacts` stays excluded
 * (dead field). No "likely trash/archive later" tag (no scheduled
 * reclassification exists).
 */
export const ExplanationTagSchema = z.enum([
  'unread',
  'starred',
  'important',
  'within_freshness_window',
  'prior_reply_thread',
  'action_needed',
  'kept_for_safety_review',
  'archived_conservatively',
  'already_archived',
  'category_unclear',
  'expired_code',
  'fresh_code',
]);
export type ExplanationTag = z.infer<typeof ExplanationTagSchema>;

/**
 * Classification Actions
 * Gmail actions to execute when suggestion is applied
 */

export const ClassificationActionsSchema = z.object({
  keep_unread: z.boolean().default(false).describe('Keep message marked as unread'),
  move_to_inbox: z.boolean().default(false).describe('Move to inbox if archived'),
  archive: z.boolean().default(false).describe('Archive the message'),
  trash: z.boolean().default(false).describe('Move to trash'),
})

export type ClassificationActions = z.infer<typeof ClassificationActionsSchema>

/**
 * Classification Result
 * Output of classification logic (deterministic or LLM)
 */

/**
 * Two-Tier Label & Safety Metadata (issue #335)
 *
 * Optional explainability metadata produced by the LLM:
 *   - `label_freetext`: short human-readable label (LLM-required at output)
 *   - `label_canonical`: mapping back to the canonical SMART_TRACK_LABELS
 *     taxonomy when the LLM is confident
 *   - `risk_type`, `event_state`, `action_required`: top-level mirrors of
 *     the corresponding `signals.*` fields so downstream consumers can read
 *     the safety/lifecycle posture without walking the signals object.
 *
 * Every field is optional/nullable so legacy rows and Pre-LLM deterministic
 * results continue to parse cleanly without backfill.
 */
export const SuggestionLabelMetadataSchema = z.object({
  label_freetext: z
    .string()
    .min(1)
    .max(80)
    .optional()
    .describe('Short free-text human-readable label produced by the LLM (max 80 chars)'),
  label_canonical: z
    .string()
    .max(80)
    .nullable()
    .optional()
    .describe('Canonical label from SMART_TRACK_LABELS if confidently mapped'),
  risk_type: RiskTypeSchema
    .nullable()
    .optional()
    .describe('Top-level mirror of signals.risk_type'),
  event_state: z
    .enum(['pending', 'in_progress', 'expired', 'closed', 'na', 'unknown'])
    .nullable()
    .optional()
    .describe('Lifecycle state of the event (mirrors signals.event_status)'),
  action_required: z
    .boolean()
    .nullable()
    .optional()
    .describe('True when the user must take an explicit action (mirrors signals.needs_user_action)'),
})

export type SuggestionLabelMetadata = z.infer<typeof SuggestionLabelMetadataSchema>

export const ClassificationResultSchema = z.object({
  message_id: z.string().describe('Gmail message ID'),
  track: TrackSchema.describe('Recommended track for the email'),
  labels: z
    .array(z.string())
    .max(3)
    .default([])
    .describe('Label names to apply (max 3)'),
  actions: ClassificationActionsSchema.describe('Actions to execute on the message'),
  signals: ClassificationSignalsSchema.describe('Classification signals'),
  confidence: z
    .number()
    .min(0)
    .max(1)
    .describe('Confidence score (0.0-1.0)'),
  rationale: z.string().describe('Human-readable explanation'),
  phase1_match: z.boolean().default(false).describe('Was deterministic rule matched'),
  llm_used: z.boolean().default(false).describe('Was LLM used for classification'),
  matchedRuleId: z.string().uuid().describe('ID of the rule that matched (or LLM sentinel UUID)'),
  llm_metadata: z
    .record(z.string(), z.any())
    .default({})
    .describe('LLM-extracted metadata for Post-LLM rule matching (event_status, event_type, event_date)'),
  applied_at: z
    .string()
    .datetime()
    .optional()
    .describe('ISO timestamp when applied'),
}).merge(SuggestionLabelMetadataSchema)

export type ClassificationResult = z.infer<typeof ClassificationResultSchema>

/**
 * Strict schema for the raw JSON the LLM returns.
 * Absolute and relative date parts are extraction-only; the backend derives
 * event_date, lifecycle state, and has_future_event deterministically.
 *
 * Pre-LLM deterministic rules may produce 0 labels, so the base
 * ClassificationResultSchema remains more permissive.
 */
export const LLMRawOutputSignalsSchema = z.object({
  is_list_mail: z.boolean(),
  is_marketing: z.boolean(),
  requires_action: z.boolean(),
  action_type: ActionTypeSchema,
  auth_event_type: AuthEventTypeSchema.nullable().optional(),
  obligation_state: ObligationStateSchema.nullable().optional(),
  content_disposition: ContentDispositionSchema.nullable().optional(),
  risk_type: RiskTypeSchema,
  is_transaction_record: z.boolean(),
  institution_type: z.enum(['banking', 'insurance', 'government']).nullable(),
  event_status: z.enum(['pending', 'in_progress', 'expired', 'closed', 'na', 'unknown']).optional(),
  event_type: z.enum(['delivery', 'deadline', 'calendar_event', 'subscription', 'payment', 'other']).nullable(),
  event_date: z.string().datetime().nullable().optional(),
  event_date_parts: z
    .object({
      year: z.number().int().min(1970).max(2100).nullable(),
      month: z.number().int().min(1).max(12).nullable(),
      day: z.number().int().min(1).max(31).nullable(),
      hour: z.number().int().min(0).max(23).nullable(),
      minute: z.number().int().min(0).max(59).nullable(),
      tz: z.string().max(40).nullable(),
      precision: z.enum(['year', 'month', 'day', 'hour', 'minute']),
    })
    .nullable()
    .optional(),
  event_relative_parts: z
    .object({
      anchor: z.literal('received_at'),
      offset_value: z.number().int().min(-3660).max(3660),
      offset_unit: z.enum(['calendar_days', 'hours', 'minutes']),
      hour: z.number().int().min(0).max(23).nullable(),
      minute: z.number().int().min(0).max(59).nullable(),
      tz: z.string().max(40).nullable(),
      precision: z.enum(['day', 'hour', 'minute']),
      source_text: z.string().min(1).max(200),
    })
    .nullable()
    .optional(),
  key_evidence: z.array(z.string()),
  needs_user_action: z.boolean(),
  has_future_event: z.boolean().optional(),
})

export type LLMRawOutputSignals = z.infer<typeof LLMRawOutputSignalsSchema>

export const LLMClassificationOutputSchema = z.object({
  message_id: z.string(),
  track: TrackSchema,
  labels: z.array(z.string()).min(1).max(3).describe('LLM must produce 1-3 labels'),
  actions: ClassificationActionsSchema,
  signals: LLMRawOutputSignalsSchema,
  confidence: z.number().min(0).max(1),
  rationale: z.string().max(500).describe('LLM rationale (0-500 chars)'),
  phase1_match: z.boolean().default(false),
  llm_used: z.boolean().default(false),
  matchedRuleId: z.string().uuid(),
  llm_metadata: z.record(z.string(), z.any()).default({}),
  applied_at: z.string().datetime().optional(),
  // Two-tier label & safety metadata (issue #335). `label_freetext` is the
  // ONLY new field required at the LLM contract; the rest stay optional so
  // legacy / partial responses keep parsing and persistence stays purely
  // additive.
  label_freetext: z
    .string()
    .min(1)
    .max(80)
    .describe('Required: short free-text human-readable label (max 80 chars)'),
  label_canonical: z.string().max(80).nullable().optional(),
  risk_type: RiskTypeSchema.nullable().optional(),
  event_state: z
    .enum(['pending', 'in_progress', 'expired', 'closed', 'na', 'unknown'])
    .nullable()
    .optional(),
  action_required: z.boolean().nullable().optional(),
})

export type LLMClassificationOutput = z.infer<typeof LLMClassificationOutputSchema>

/**
 * Request Schemas
 */

export const GenerateSuggestionRequestSchema = z.object({
  message_id: z.string().describe('Gmail message ID to classify'),
  force_llm: z.boolean().default(false).optional().describe('Skip deterministic rules'),
  // Issue #721 (D3): single shared visibility policy. Defaults to the canonical
  // CLASSIFICATION_MIN_VISIBLE_CONFIDENCE (0.5) so the explicit-classify path
  // uses the same threshold as scan/realtime/reanalyze. A result at or above it
  // is a user-visible `pending` suggestion; below it is persisted as a hidden
  // `low_confidence` bookkeeping row and the route returns a non-success body.
  min_confidence: z
    .number()
    .min(0)
    .max(1)
    .default(CLASSIFICATION_MIN_VISIBLE_CONFIDENCE)
    .optional()
    .describe('Minimum confidence threshold for a user-visible suggestion (default: shared 0.5 visibility threshold)'),
})

export type GenerateSuggestionRequest = z.infer<typeof GenerateSuggestionRequestSchema>

export const ListSuggestionsRequestSchema = z.object({
  status: z.enum(['pending', 'applied', 'rejected']).optional().describe('Filter by status'),
  track: TrackSchema.optional().describe('Filter by track'),
  scanJobId: z.string().uuid().optional().describe('Filter to suggestions generated by one scan job'),
  search: z.string().optional().describe('Search in subject, from_address, snippet using ILIKE'),
  label: z
    .string()
    .min(1)
    .max(120)
    .optional()
    .describe('Filter to a single category (matches labels[0]). Pass the "Uncategorized" sentinel to select rows with no labels.'),
  limit: z.coerce.number().int().min(1).max(100).default(50).optional().describe('Max results'),
  offset: z.coerce.number().int().min(0).default(0).optional().describe('Pagination offset'),
  // (#296 / #389) Display-scale floor. Frontend sends 0.80-1.0 (display scale)
  // backend translates back to raw before querying.
  confidenceFloor: z
    .coerce
    .number()
    .min(0.80)
    .max(1)
    .default(0.80)
    .optional()
    .describe('Minimum display confidence (0.80-1.0). Maps to raw 0.50-1.0 internally.'),
});

export type ListSuggestionsRequest = z.infer<typeof ListSuggestionsRequestSchema>;

export const GetSuggestionStatsRequestSchema = z.object({
  status: z.enum(['pending', 'applied', 'rejected']).optional().describe('Filter by status'),
  track: TrackSchema.optional().describe('Filter by track'),
  scanJobId: z.string().uuid().optional().describe('Filter to suggestions generated by one scan job'),
  search: z.string().optional().describe('Search in subject, from_address, snippet using ILIKE'),
  label: z
    .string()
    .min(1)
    .max(120)
    .optional()
    .describe('Filter to a single category (matches labels[0]). Pass the "Uncategorized" sentinel to select rows with no labels.'),
  confidenceFloor: z
    .coerce
    .number()
    .min(0.80)
    .max(1)
    .default(0.80)
    .optional()
    .describe('Minimum display confidence (0.80-1.0). Maps to raw 0.50-1.0 internally.'),
});

export type GetSuggestionStatsRequest = z.infer<typeof GetSuggestionStatsRequestSchema>;

export const ApplyClassificationRequestSchema = z.object({
  action_overrides: ClassificationActionsSchema.partial().optional().describe('Override default actions'),
})

export type ApplyClassificationRequest = z.infer<typeof ApplyClassificationRequestSchema>

export const ClassifySingleRequestSchema = z.object({
  message_id: z.string().describe('Gmail message ID to classify'),
  force_llm: z.boolean().default(false).optional().describe('Skip deterministic rules'),
  return_preview: z
    .boolean()
    .default(false)
    .optional()
    .describe('Include email preview in response'),
})

export type ClassifySingleRequest = z.infer<typeof ClassifySingleRequestSchema>

export const ClassifyBatchRequestSchema = z.object({
  message_ids: z
    .array(z.string())
    .max(50)
    .describe('Gmail message IDs (max 50)'),
  apply_actions: z.boolean().default(false).optional().describe('Immediately apply actions'),
  force_llm: z.boolean().default(false).optional().describe('Skip deterministic rules'),
})

export type ClassifyBatchRequest = z.infer<typeof ClassifyBatchRequestSchema>

export const ClassificationFeedbackSchema = z.object({
  message_id: z.string().describe('Gmail message ID'),
  operation_id: z.string().uuid().describe('Links to activity_logs operation'),
  correct_track: TrackSchema.describe('What track should it have been'),
  user_comment: z.string().optional().describe('User feedback comment'),
})

export type ClassificationFeedback = z.infer<typeof ClassificationFeedbackSchema>

/**
 * Response Schemas
 */

export const ClassifyBatchResponseSchema = z.object({
  results: z.array(ClassificationResultSchema),
  summary: z.object({
    total: z.number(),
    succeeded: z.number(),
    failed: z.number(),
    processing_time_ms: z.number(),
  }),
})

export type ClassifyBatchResponse = z.infer<typeof ClassifyBatchResponseSchema>

/**
 * Email Preview
 */

export const ListEmailsRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50).optional().describe('Max emails to preview'),
  source: z.enum(['db', 'gmail']).default('db').optional().describe('Where to pull emails from'),
})

export type ListEmailsRequest = z.infer<typeof ListEmailsRequestSchema>

export const ListEmailsResponseSchema = z.object({
  source: z.enum(['db', 'gmail']).describe('Actual preview source used'),
  count: z.number().describe('Number of emails returned'),
  emails: z.array(EmailPreviewSchema).describe('Email previews'),
})

export type ListEmailsResponse = z.infer<typeof ListEmailsResponseSchema>

/**
 * Sender behavioral context (issue #511, Tier B; extended in #519).
 *
 * A passthrough of already-computed `sender_insights` columns. Carried ONLY on
 * the suggestion-detail response (a companion fetch when the email-detail panel
 * opens) so the list payload stays small and the list never triggers an N+1
 * per-sender lookup. Optional everywhere - when absent the panel renders Tier A
 * (rationale + safe chips) with no placeholder.
 *
 * #519 added `open_rate` and `last_recalculated_at` so the email-detail
 * sender-profile stats view can show all four behavior rates plus an honest
 * "Insights updated" metadata line. No DB migration was needed - both columns
 * already exist on `sender_insights`.
 *
 * Rates are `null` when `total_emails_received < 4` (the shared sender-history
 * support floor). The backend never invents values; the frontend maps the raw stored rate
 * to a qualitative bucket ("almost always / usually / sometimes") and renders the
 * "Not enough history yet" state when history is thin. The contract
 * carries the raw stored rate only - no bucket logic lives here.
 */
export const SenderContextSchema = z.object({
  open_rate: z.number().nullable().describe('Fraction (0..1) of this sender\'s emails opened over full history; null below support floor'),
  archive_rate: z.number().nullable().describe('Fraction (0..1) of this sender\'s emails archived over full history; null below support floor'),
  trash_rate: z.number().nullable().describe('Fraction (0..1) of this sender\'s emails trashed over full history; null below support floor'),
  reply_rate: z.number().nullable().describe('Fraction (0..1) of this sender\'s emails replied to over full history; null below support floor'),
  total_emails_received: z.number().int().nonnegative().describe('Lifetime count of this sender\'s messages; rates stay null until this reaches 4'),
  is_in_contacts: z.boolean().describe('Sender is in the user\'s Google contacts'),
  has_prior_reply_thread: z.boolean().describe('User has previously replied in a thread with this sender'),
  /**
   * When the sender_insights row was last recomputed (ISO timestamp, nullable).
   * Surfaced verbatim so the panel can render an "Insights updated" metadata line.
   * Stored as a Postgres timestamptz string, so kept as a plain nullable string
   * (not `.datetime()`) to avoid rejecting the `+00:00` offset PostgREST returns.
   */
  last_recalculated_at: z.string().nullable().describe('ISO timestamp of the last sender_insights recompute; null if never recalculated'),
})

export type SenderContext = z.infer<typeof SenderContextSchema>

/**
 * Classification Suggestion DTOs (ts-rest responses)
 */
export const ClassificationSuggestionSchema = z.object({
  id: z.string(),
  message_id: z.string(),
  email_subject: z.string().nullable().optional(),
  sender_email: z.string().nullable().optional(),
  snippet: z.string().nullable().optional(),
  track: TrackSchema,
  labels: z.array(z.string()).default([]),
  actions: ClassificationActionsSchema,
  signals: ClassificationSignalsSchema.optional(),
  confidence: z.number(),
  display_confidence: z.number().optional(),
  rationale: z.string(),
  phase1_match: z.boolean().default(false),
  llm_used: z.boolean().default(false),
  status: z.enum(['pending', 'applied', 'rejected', 'expired']).default('pending'),
  /**
   * Decision provenance (issue #721) - additive, nullable. Legacy rows persisted
   * before the migration parse unchanged (all optional). Written identically by
   * the per-message (`persistSuggestion`) and batch (`persistExternalResult`)
   * paths.
   */
  decision_source: DecisionSourceSchema.nullable().optional(),
  decision_path: DecisionPathSchema.nullable().optional(),
  applied_policy_id: z.string().uuid().nullable().optional(),
  safety_reason_code: SafetyReasonCodeSchema.nullable().optional(),
  decision_provenance_version: z.number().int().nonnegative().optional(),
  /** When PureBox created the suggestion (scan time) - not the email's date. */
  created_at: z.string().datetime().optional(),
  /** Real date the email was received in Gmail (from internalDate). */
  received_at: z.string().datetime().optional(),
  applied_at: z.string().datetime().optional(),
  operation_id: z.string().optional(),
  /**
   * Sender behavioral context (#511, Tier B). Optional - only populated on the
   * suggestion-detail response (companion fetch), never on the list. Absence
   * means render Tier A only.
   */
  sender_context: SenderContextSchema.optional(),
  /**
   * Whitelisted Gmail system-label state. Filtered to STARRED / IMPORTANT /
   * UNREAD / CATEGORY_PROMOTIONS / INBOX and available on list and detail
   * responses when email metadata exists.
   */
  gmail_labels: z.array(z.string()).optional(),
  /**
   * Additive decision-context tags (issue #1017) explaining why a suggestion
   * stayed visible or was deferred (e.g. "Unread", "Kept for safety review").
   * Read-time derived, never fed back into classification. Defaults to an
   * empty array so legacy/pre-feature reads never fail parsing. `prior_reply_thread`
   * only appears on detail responses (needs `sender_context`, Tier B only).
   */
  explanation_tags: z.array(ExplanationTagSchema).optional().default([]),
}).merge(SuggestionLabelMetadataSchema)

export type ClassificationSuggestion = z.infer<typeof ClassificationSuggestionSchema>

export const GenerateClassificationSuggestionResponseSchema = z.object({
  suggestion_id: z.string(),
  message_id: z.string(),
  track: TrackSchema,
  labels: z.array(z.string()),
  actions: ClassificationActionsSchema,
  confidence: z.number(),
  rationale: z.string(),
  phase1_match: z.boolean(),
  llm_used: z.boolean(),
  matchedRuleId: z.string().uuid(),
})

export type GenerateClassificationSuggestionResponse = z.infer<typeof GenerateClassificationSuggestionResponseSchema>

/**
 * Per-(track, label) aggregate: the count of pending suggestions in a category
 * and the reclaimable size in bytes. Used by `summary.by_label` (#476).
 */
export const LabelAggregateSchema = z.object({
  count: z.number().int().nonnegative(),
  size_bytes: z.number().int().nonnegative(),
})

export type LabelAggregate = z.infer<typeof LabelAggregateSchema>

export const ListClassificationSuggestionsResponseSchema = z.object({
  suggestions: z.array(ClassificationSuggestionSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  summary: z.object({
    by_track: z.record(z.string(), z.number()).optional(),
    by_status: z.record(z.string(), z.number()).optional(),
    avg_confidence: z.number().optional(),
    /**
     * Aggregate size in bytes of all suggestions matching the listing
     * filters (issue #390). Computed via LEFT JOIN to emails so missing
     * rows contribute 0. Optional / additive.
     */
    total_size_bytes: z.number().int().nonnegative().optional(),
    /**
     * Per-track size aggregates, mirroring `by_track`. Lets the cleanup
     * ribbon show storage per pill from a single unfiltered call instead
     * of three separate filtered requests.
     */
    size_by_track: z.record(z.string(), z.number()).optional(),
    /**
     * Per-(track, label) count + reclaimable size (#476). Outer key = track
     * name (Attention | Archive | Trash), inner key = labels[0] (or the
     * "Uncategorized" sentinel). The sum of inner counts within a track
     * equals by_track[track]. Only present when offset === 0 so load-more
     * never recomputes it. Optional / additive.
     */
    by_label: z
      .record(z.string(), z.record(z.string(), LabelAggregateSchema))
      .optional()
      .describe('Per-(track,label) count + reclaimable size. Outer key = track name, inner key = labels[0] (or "Uncategorized"). Sum of inner counts within a track === by_track[track]. Only present when offset === 0.'),
  }).optional(),
});

export type ListClassificationSuggestionsResponse = z.infer<typeof ListClassificationSuggestionsResponseSchema>;

export const SuggestionStatsResponseSchema = z.object({
  total: z.number().int().nonnegative(),
  by_track: z.record(z.string(), z.number()),
  by_status: z.record(z.string(), z.number()),
  avg_confidence: z.number(),
  total_size_bytes: z.number().int().nonnegative(),
  size_by_track: z.record(z.string(), z.number()),
  by_label: z
    .record(z.string(), z.record(z.string(), LabelAggregateSchema))
    .optional()
    .describe('Per-(track,label) count + reclaimable size. Outer key = track name, inner key = labels[0] (or "Uncategorized"). Sum of inner counts within a track === by_track[track].'),
});

export type SuggestionStatsResponse = z.infer<typeof SuggestionStatsResponseSchema>;

export const GetClassificationSuggestionResponseSchema = ClassificationSuggestionSchema
export type GetClassificationSuggestionResponse = z.infer<typeof GetClassificationSuggestionResponseSchema>

export const ApplyClassificationSuggestionResponseSchema = z.object({
  suggestion_id: z.string(),
  message_id: z.string(),
  track: TrackSchema,
  actions_applied: z.object({
    archived: z.boolean(),
    trashed: z.boolean(),
    marked_read: z.boolean(),
    labels_applied: z.array(z.string()),
  }),
  operation_id: z.string(),
  timestamp: z.string().datetime(),
})

export type ApplyClassificationSuggestionResponse = z.infer<typeof ApplyClassificationSuggestionResponseSchema>

export const RejectClassificationSuggestionResponseSchema = z.object({
  suggestion_id: z.string(),
  status: z.literal('rejected'),
  message: z.string(),
})

export type RejectClassificationSuggestionResponse = z.infer<typeof RejectClassificationSuggestionResponseSchema>

/* ------------------------------------------------------------------ */
/* Cluster apply (#350)                                                */
/* ------------------------------------------------------------------ */

export const ClusterKindSchema = z.enum(['sender', 'rationale'])
export type ClusterKind = z.infer<typeof ClusterKindSchema>

export const CLUSTER_APPLY_MAX_SUGGESTIONS = 200

export const ApplyClusterRequestSchema = z.object({
  suggestion_ids: z
    .array(z.string().uuid())
    .min(1)
    .max(CLUSTER_APPLY_MAX_SUGGESTIONS)
    .describe('Pending suggestion IDs that share a sender or rationale.'),
  cluster_kind: ClusterKindSchema.describe('Cluster grouping dimension.'),
  cluster_key: z
    .string()
    .min(1)
    .max(500)
    .describe('Sender email or normalised rationale text identifying the cluster.'),
})
export type ApplyClusterRequest = z.infer<typeof ApplyClusterRequestSchema>

export const ClusterApplyResultStatusSchema = z.enum(['applied', 'failed', 'skipped'])
export type ClusterApplyResultStatus = z.infer<typeof ClusterApplyResultStatusSchema>

export const ClusterApplyResultItemSchema = z.object({
  suggestion_id: z.string(),
  status: ClusterApplyResultStatusSchema,
  message_id: z.string().nullable(),
  error_code: z.string().nullable(),
  error_message: z.string().nullable(),
})
export type ClusterApplyResultItem = z.infer<typeof ClusterApplyResultItemSchema>

export const ApplyClusterResponseSchema = z.object({
  operation_id: z.string(),
  cluster_kind: ClusterKindSchema,
  cluster_key: z.string(),
  applied_count: z.number().int().min(0),
  failed_count: z.number().int().min(0),
  skipped_count: z.number().int().min(0),
  results: z.array(ClusterApplyResultItemSchema),
  applied_at: z.string().datetime(),
})
export type ApplyClusterResponse = z.infer<typeof ApplyClusterResponseSchema>

export const ReassignTrackRequestSchema = z.object({
  track: TrackSchema.describe('New track to assign the suggestion to'),
})

export type ReassignTrackRequest = z.infer<typeof ReassignTrackRequestSchema>

export const ReassignTrackResponseSchema = z.object({
  suggestion_id: z.string(),
  old_track: TrackSchema,
  new_track: TrackSchema,
})

export type ReassignTrackResponse = z.infer<typeof ReassignTrackResponseSchema>

export const BATCH_REASSIGN_MAX_SUGGESTIONS = 200;

export const BatchReassignTrackRequestSchema = z.object({
  anchor_suggestion_id: z.string().uuid(),
  suggestion_ids: z.array(z.string().uuid()).min(1).max(BATCH_REASSIGN_MAX_SUGGESTIONS),
  track: TrackSchema,
}).superRefine((value, context) => {
  if (value.suggestion_ids.every((id) => id === value.anchor_suggestion_id)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['suggestion_ids'],
      message: 'At least one peer suggestion is required',
    });
  }
});

export type BatchReassignTrackRequest = z.infer<typeof BatchReassignTrackRequestSchema>

export const BatchReassignTrackResultSchema = z.object({
  suggestion_id: z.string().uuid(),
  status: z.enum(['updated', 'failed', 'skipped']),
  error_code: z.string().nullable(),
  error_message: z.string().nullable(),
});

export type BatchReassignTrackResult = z.infer<typeof BatchReassignTrackResultSchema>

export const BatchReassignTrackResponseSchema = z.object({
  anchor_suggestion_id: z.string().uuid(),
  target_track: TrackSchema,
  updated_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
  skipped_count: z.number().int().nonnegative(),
  results: z.array(BatchReassignTrackResultSchema),
});

export type BatchReassignTrackResponse = z.infer<typeof BatchReassignTrackResponseSchema>

export const ReanalyzeClassificationSuggestionResponseSchema = z.object({
  suggestion_id: z.string(),
  message_id: z.string(),
  old_track: TrackSchema,
  new_track: TrackSchema,
  old_confidence: z.number(),
  new_confidence: z.number(),
  old_actions: ClassificationActionsSchema,
  new_actions: ClassificationActionsSchema,
  old_labels: z.array(z.string()),
  new_labels: z.array(z.string()),
  changed: z.boolean().describe('Whether the suggestion changed after reanalysis'),
  rationale: z.string().describe('New rationale based on updated rules'),
  phase1_match: z.boolean(),
  llm_used: z.boolean(),
  timestamp: z.string().datetime(),
})

export type ReanalyzeClassificationSuggestionResponse = z.infer<typeof ReanalyzeClassificationSuggestionResponseSchema>

export const ReanalyzeAllSuggestionsResponseSchema = z.object({
  total_pending: z.number().describe('Total pending suggestions found'),
  reanalyzed: z.number().describe('How many suggestions were reprocessed'),
  changed: z.number().describe('How many suggestions changed after reanalysis'),
  errors: z
    .array(
      z.object({
        suggestion_id: z.string(),
        message: z.string(),
        code: z.string().optional(),
      })
    )
    .optional()
    .describe('Per-suggestion errors encountered during batch reanalysis'),
  duration_ms: z.number().optional().describe('Elapsed milliseconds for the batch'),
  started_at: z.string().datetime().describe('Batch start timestamp'),
  finished_at: z.string().datetime().describe('Batch completion timestamp'),
})

export type ReanalyzeAllSuggestionsResponse = z.infer<typeof ReanalyzeAllSuggestionsResponseSchema>

/**
 * DEV-ONLY. Acknowledgement for the latest-scan sample reclassification background run.
 * The endpoint returns as soon as the run is accepted - it never waits for the
 * classification loop to finish, so callers get `queued` (how many persisted sample
 * targets, capped at 100, the run will walk) rather than per-email results.
 */
export const ReclassifyAllEmailsResponseSchema = z.object({
  started: z.boolean().describe('True when a new background reclassification run was accepted'),
  queued: z.number().describe('Number of persisted latest-scan sample targets the background run will reclassify'),
  already_running: z
    .boolean()
    .describe('True when a run for this user was already in flight - nothing new was queued'),
  started_at: z.string().datetime().describe('Timestamp the run was accepted'),
})

export type ReclassifyAllEmailsResponse = z.infer<typeof ReclassifyAllEmailsResponseSchema>

/**
 * User Feedback on Classification Suggestions (Phase 5B.1)
 */

export const UserVerdictSchema = z.enum(['accepted', 'rejected', 'overridden'])
export type UserVerdict = z.infer<typeof UserVerdictSchema>

/**
 * Reject reason enum (legacy / reserved).
 *
 * Note: as of issue #283 (Slice 2) no UI surface captures `reject_reason` or
 * `reject_reason_text`. The values below are kept for backward compatibility
 * with already-stored rows and for potential future reuse. The `too_aggressive`
 * value was dropped because we explicitly chose not to build a reason picker.
 */
export const RejectReasonSchema = z.enum(['wrong_track', 'wrong_labels', 'not_useful', 'other'])
export type RejectReason = z.infer<typeof RejectReasonSchema>

export const SuggestionFeedbackRequestSchema = z.object({
  verdict: UserVerdictSchema,
  /** Not currently captured by any UI; reserved. See RejectReasonSchema docs. */
  reject_reason: RejectReasonSchema.optional(),
  /** Not currently captured by any UI; reserved. See RejectReasonSchema docs. */
  reject_reason_text: z.string().max(500).optional(),
  override_track: TrackSchema.optional(),
}).refine(
  (data) => data.verdict !== 'rejected' || data.reject_reason != null,
  { message: 'reject_reason is required when verdict is rejected' }
).refine(
  (data) => data.verdict !== 'overridden' || data.override_track != null,
  { message: 'override_track is required when verdict is overridden' }
)

export type SuggestionFeedbackRequest = z.infer<typeof SuggestionFeedbackRequestSchema>

export const SuggestionFeedbackResponseSchema = z.object({
  suggestion_id: z.string(),
  verdict: UserVerdictSchema,
  feedback_at: z.string().datetime(),
})

export type SuggestionFeedbackResponse = z.infer<typeof SuggestionFeedbackResponseSchema>
